import Dayjs from 'dayjs'
import { isEmpty } from 'lodash'

import { enrolmentTypes } from 'app/config'
import constants from 'app/constants'
import { BOOKING_PAYMENT_TYPES } from 'app/pages/Class/Inventory/constants'
import {
  bookEnrolmentBodyStructure,
  bookEnrolmentWithEmandateBodyStructure,
  bookEnrolmentWithRechargeBodyStructure,
  bookProgramBodyStructure,
  categoryCostBodyStructure,
  coinCalculationBodyStructure,
  setupEmandateAndBookBodyStructure
} from 'app/pages/Class/Inventory/transformers'
import { sentryLogger } from 'sentry'

import { apiEndpoints } from './common'

import { makeRequestUrl, showToast } from '../app/helpers'
import { get, post } from '../request'

const bookingRequestStructure = (type, batch, formData, userData) => ({
  batch: batch.uuid,
  student_type: batch.studentSource.value,
  student_uuid: userData.student_uuid,
  start_date: batch.startDate?.label,
  payment_mode: 'pay_custom_fee',
  type,
  ...(type === enrolmentTypes.CLASS
    ? {
        fee: batch.fee,
        frequency: formData.payment_frequency,
        discount_percent: formData.discountPercentageValue,
        first_invoice: {
          start_date: batch.startDate?.label,
          end_date: Dayjs(batch.startDate?.label)
            .endOf('month')
            .format('YYYY-MM-DD'),
          fee: batch.firstTimeFee
        }
      }
    : type === enrolmentTypes.MEMBERSHIP && batch.type === 'explore'
    ? {
        end_date: batch.startDate?.label
      }
    : null)
})

const membershipBookingRequestStructure = (type, batchInfo, userData) => ({
  batches: !isEmpty(batchInfo)
    ? Object.keys(batchInfo)?.map((batchId) => {
        return {
          batch: batchId,
          student_type: 'openhouse',
          student_uuid: userData.student_uuid,
          type
        }
      })
    : [],
  parent_id: userData.parent,
  book_ongoing: type === 'membership' ? true : false,
  intent: Object.values(batchInfo)[0]?.intent
    ? Object.values(batchInfo)[0]?.intent
    : null
})

export const fetchInventorySchedules = async ({ url, queries } = {}) => {
  const requestUrl = !isEmpty(url)
    ? url
    : makeRequestUrl(`${apiEndpoints.batch}/`, queries)

  try {
    const response = await get(requestUrl)

    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching inventory schedules')
    throw error
  }
}

export const createDemoClassBooking = async ({
  type,
  batchInfo,
  formData,
  userData
}) => {
  try {
    await Promise.all(
      Object.keys(batchInfo).map((batchId) =>
        post(
          apiEndpoints.enrolment,
          bookingRequestStructure(
            type,
            { uuid: batchId, ...batchInfo[batchId] },
            formData,
            userData
          )
        )
      )
    )

    return {
      success: true
    }
  } catch (error) {
    sentryLogger(error, 'error in creating demo class booking')
    throw error
  }
}

export const createClassBooking = async ({ type, batchInfo, userData }) => {
  console.log(type, batchInfo, userData)

  try {
    await Promise.resolve(
      post(
        `${apiEndpoints.cart}/create/`,
        membershipBookingRequestStructure(type, batchInfo, userData)
      )
    )

    return {
      success: true
    }
  } catch (error) {
    sentryLogger(error, 'error in creating class booking')
    throw error
  }
}

export const getBatchEligibilityForStudent = async ({
  student,
  batch,
  startDate
}) => {
  try {
    if (!student || !batch) throw new Error('Student and batch are required')
    const requestUrl = makeRequestUrl(`${apiEndpoints.batch}/eligibility/`)
    // `start_date` (YYYY-MM-DD) is optional. Without it the backend answers
    // "is this batch free from today", which refuses a booking whose start is
    // deliberately weeks out — a class that ends before the child joins is not
    // a clash. Send it as soon as a date has been picked.
    const response = await post(requestUrl, {
      student,
      batch,
      ...(startDate ? { start_date: startDate } : {})
    })

    return response?.data
  } catch (error) {
    sentryLogger(error, 'error in getting batch eligibility for student')
    throw error
  }
}

export const getCoinCalculation = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.coinCalculation}`,
      coinCalculationBodyStructure(data)
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching monthly consumption')
    throw error
  }
}

export const getCategoryCost = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.subscription}calculate_cost/`,
      categoryCostBodyStructure(data)
    )
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching prorata batch cost')
    throw error
  }
}

export const handleBookEnrolment = async (data) => {
  const { payment_type } = data
  try {
    let res
    switch (payment_type) {
      case BOOKING_PAYMENT_TYPES.BOOK_WITH_COINS:
        res = await bookEnrolment({
          ...data,
          payment_mode: 'coins'
        })
        break

      case BOOKING_PAYMENT_TYPES.BOOK_WITH_RUPEES:
        res = await bookEnrolment({
          ...data,
          payment_mode: 'rupees'
        })
        break

      case BOOKING_PAYMENT_TYPES.RECHARGE_AND_PAY:
        res = await bookEnrolmentWithRecharge({
          ...data
        })
        break

      case BOOKING_PAYMENT_TYPES.BOOK_WITH_EMANDATE:
        res = await bookEnrolmentWithEmandate({
          ...data
        })
        break

      case BOOKING_PAYMENT_TYPES.SETUP_EMANDATE_AND_PAY:
        res = await setupEmandateAndBook({
          ...data
        })
        break

      case BOOKING_PAYMENT_TYPES.PROGRAM_RUPEES:
        res = await bookProgramCart({
          ...data
        })

      default:
        break
    }

    return res
  } catch (error) {
    sentryLogger(error)
    throw error
  }
}

const bookEnrolment = async (data) => {
  try {
    const makeRequestUrl = `${apiEndpoints.cartV2}`
    const response = await post(
      makeRequestUrl,
      bookEnrolmentBodyStructure(data)
    )
    return response?.data
  } catch (error) {
    sentryLogger(error, 'error in booking enrolment with coins')
    throw error
  }
}

// Both e-mandate flows post to the same cart endpoint as every other booking; the
// `payment_type` in the body is what routes them to the v3 cart API server-side.
const bookEnrolmentWithEmandate = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.cartV2}`,
      bookEnrolmentWithEmandateBodyStructure(data)
    )
    return response?.data
  } catch (error) {
    sentryLogger(error, 'error in booking enrolment with e-mandate')
    throw error
  }
}

const setupEmandateAndBook = async (data) => {
  try {
    const response = await post(
      `${apiEndpoints.cartV2}`,
      setupEmandateAndBookBodyStructure(data)
    )
    return response?.data
  } catch (error) {
    sentryLogger(error, 'error in setting up e-mandate and booking')
    throw error
  }
}

const bookEnrolmentWithRecharge = async (data) => {
  try {
    const makeRequestUrl = `${apiEndpoints.cartV2}`
    const response = await post(
      makeRequestUrl,
      bookEnrolmentWithRechargeBodyStructure(data)
    )
    return response?.data
  } catch (error) {
    sentryLogger(error, 'error in booking enrolment with coins')
    throw error
  }
}

const bookProgramCart = async (data) => {
  try {
    const makeRequestUrl = `${apiEndpoints.cartV2}`
    const response = await post(makeRequestUrl, bookProgramBodyStructure(data))
    return response?.data
  } catch (error) {
    sentryLogger(error, 'error in booking enrolment with coins')
    throw error
  }
}

export const getCartDetails = async (data) => {
  const makeRequestUrl = `${apiEndpoints.orders}/${data?.invoiceId}`
  try {
    const response = await get(makeRequestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching cart details')
    throw error
  }
}

export const getPlayCareEligibleGrades = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'PLAY_CARE_ELIGIBLE_GRADES'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log(error, 'error in fetching deduction reasons')
    throw error
  }
}

export const getInventoryPrograms = async ({ url, queries }) => {
  const requestUrl = makeRequestUrl(
    `${apiEndpoints.programs}/programs/config`,
    queries
  )
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    sentryLogger(error, 'error in fetching cart details')
    throw error
  }
}

export const getProgramBatches = async ({ url, queries }) => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.batch}`, queries)
  try {
    const response = await get(requestUrl)
    return response
  } catch (error) {
    sentryLogger(error, 'error in fetching program batches')
    throw error
  }
}

export const getProgramFee = async ({ url, queries }) => {
  const requestUrl = makeRequestUrl(
    `${apiEndpoints.programs}/programs/fee`,
    queries
  )
  try {
    const response = await get(requestUrl)
    return response
  } catch (error) {
    sentryLogger(error, 'error in fetching program fees')
    throw error
  }
}

export const getPreschoolPaymentSummary = async ({ url, payload }) => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.preschoolProgramSummary}`)
  try {
    const response = await post(requestUrl, payload)
    return response
  } catch (error) {
    showToast(error, constants.ERROR)
    sentryLogger(error, 'error in fetching program payment summary')
    throw error
  }
}

export const fetchPaymentCycleOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'SCHOOL_CARE_PAYMENT_CYCLE_CONFIG'
  })
  try {
    const response = await get(requestUrl)
    return response.data
  } catch (error) {
    console.log(error, 'error in fetching payment cycles reasons')
    throw error
  }
}

// Feature 4 — fetches whether the logged-in user is permitted to apply a
// custom (non-catalog) discount. Backend computes this from the
// AppConfig['DISCOUNT_AUTHORIZED_PHONE_NUMBERS'] whitelist and re-checks on
// cart submission, so this flag is purely a UI affordance.
export const fetchCanApplyCustomDiscount = async () => {
  try {
    const response = await get(apiEndpoints.me)
    return Boolean(response?.data?.can_apply_custom_discount)
  } catch (error) {
    sentryLogger(error, 'fetchCanApplyCustomDiscount')
    return false
  }
}

// Feature 4 — fetches curated `Discount` rows for the cart dropdown.
// Filters are optional; passed straight through as query params.
export const fetchCuratedDiscounts = async ({
  programType,
  type,
  kind
} = {}) => {
  const params = {}
  if (programType) params.program_type = programType
  if (type) params.type = type
  if (kind) params.kind = kind
  const requestUrl = makeRequestUrl(apiEndpoints.curatedDiscounts, params)
  try {
    const response = await get(requestUrl)
    return response?.data?.discounts || []
  } catch (error) {
    sentryLogger(error, 'fetchCuratedDiscounts')
    return []
  }
}

export const fetchAdditionalDiscountOptions = async () => {
  const requestUrl = makeRequestUrl(`${apiEndpoints.configApp}`, {
    key: 'PRESCHOOL_ADDITIONAL_DISCOUNTS'
  })
  try {
    const response = await get(requestUrl)
    return response.data
    // return {
    //   count: 1,
    //   next: null,
    //   previous: null,
    //   results: [
    //     {
    //       uuid: 'bd313402-8494-450b-a248-6367b9dd33bf',
    //       key: 'PRESCHOOL_ADDITIONAL_DISCOUNTS',
    //       value: [
    //         {
    //           name: 'Additional',
    //           percentage: 5,
    //           payment_cycle: 'yearly',
    //           discount_type: 'full_payment',
    //           label: '_5% off_ on the annual price'
    //         },
    //         {
    //           name: 'Additional',
    //           percentage: 20,
    //           payment_cycle: 'quarterly',
    //           discount_type: 'first_installment',
    //           label: '_20% off_ on the first instalment'
    //         }
    //       ],
    //       version: '',
    //       app_name: ['alfred'],
    //       platform: 'all'
    //     }
    //   ],
    //   current_time: 1748944133
    // }
  } catch (error) {
    console.log(error, 'error in fetching additional discount options')
    throw error
  }
}
